import { Logo } from '@petsmart-ui/sparky';

import { SideDrawer } from '@libs/shared-ui';

import { trackNavigationHeaderClick } from '../../../../shared';
import type { MenuQuickLink, PrimaryNavigationItem } from '../../../../types';
import { NestedLevel } from './nested-level';
import { QuickLinks } from './quick-links';
import { Level, type LevelEntry } from './shared';
import { useDrawer } from './use-drawer';

import './drawer.scss';

type DrawerProps = {
    isOpen: boolean;
    items: PrimaryNavigationItem[];
    onCloseAll: () => void;
    onOpenChange: (isOpen: boolean) => void;
    onOpenChangeComplete: (isOpen: boolean) => void;
    quickLinks: MenuQuickLink[];
};

export function Drawer({ isOpen, items, onCloseAll, onOpenChange, onOpenChangeComplete, quickLinks }: DrawerProps) {
    const { activeNode, closeAll, closeButtonRef, closeChild, handleOpenChange, handleOpenChangeComplete, openNode } =
        useDrawer({ onCloseAll, onOpenChange, onOpenChangeComplete });

    const rootEntries: LevelEntry[] = items.map(item =>
        item.kind === 'link'
            ? {
                  href: item.href,
                  id: item.id,
                  label: item.title,
                  onSelect: () => {
                      trackNavigationHeaderClick({
                          selection: [item.title],
                          linkUrl: item.href,
                          navSection: 'main nav',
                      });
                      closeAll();
                  },
                  openInNewTab: item.openInNewTab,
                  sale: item.emphasis === 'promotion',
              }
            : {
                  id: item.id,
                  label: item.title,
                  onSelect: trigger => openNode(item, trigger),
                  sale: item.emphasis === 'promotion',
              }
    );

    return (
        <>
            <SideDrawer
                className="mobile-menu-drawer"
                initialFocus={closeButtonRef}
                isOpen={isOpen}
                onClose={closeAll}
                onOpenChange={handleOpenChange}
                onOpenChangeComplete={handleOpenChangeComplete}
            >
                <SideDrawer.Header closeRef={closeButtonRef} className="mobile-menu-drawer__header">
                    <SideDrawer.Title className="mobile-menu-drawer__title">Main menu</SideDrawer.Title>
                    <Logo href="/" className="mobile-menu-drawer__logo" />
                </SideDrawer.Header>
                <SideDrawer.Body>
                    <Level root entries={rootEntries} label="Main menu" />
                    <div className="mobile-menu-drawer__quick-links">
                        <QuickLinks links={quickLinks} onLinkSelect={closeAll} />
                    </div>
                </SideDrawer.Body>
            </SideDrawer>

            {activeNode && (
                <NestedLevel
                    node={activeNode}
                    rootIsOpen={isOpen}
                    selectionPath={[activeNode.title]}
                    onBack={closeChild}
                    onCloseAll={closeAll}
                />
            )}
        </>
    );
}
